/**
 * PPG Signal Processing Pipeline
 * Implements detrending, bandpass filtering, normalisation, peak detection,
 * BPM calculation, IBI extraction, and HRV metrics (RMSSD).
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PPGMetrics {
  heartRate: number;       // BPM
  ibiArray: number[];      // Inter-beat intervals in ms
  rmssd: number;           // Root mean square of successive differences (ms)
  signalQuality: number;   // 0–1 quality score
}

export interface ProcessorConfig {
  sampleRate: number;       // Hz (frames per second)
  windowSeconds: number;    // Rolling window duration
  minBPM: number;
  maxBPM: number;
}

// ─── Circular Buffer ──────────────────────────────────────────────────────────

/** Fixed-size circular buffer for efficient rolling window storage */
class CircularBuffer {
  private buf: Float64Array;
  private head = 0;
  private _size = 0;
  readonly capacity: number;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.buf = new Float64Array(capacity);
  }

  push(value: number): void {
    this.buf[this.head] = value;
    this.head = (this.head + 1) % this.capacity;
    if (this._size < this.capacity) this._size++;
  }

  /** Return values in chronological order */
  toArray(): number[] {
    if (this._size < this.capacity) {
      return Array.from(this.buf.subarray(0, this._size));
    }
    const out = new Array<number>(this.capacity);
    for (let i = 0; i < this.capacity; i++) {
      out[i] = this.buf[(this.head + i) % this.capacity];
    }
    return out;
  }

  get size(): number {
    return this._size;
  }

  isFull(): boolean {
    return this._size === this.capacity;
  }
}

// ─── Signal Processing Functions ──────────────────────────────────────────────

/**
 * Detrend signal by subtracting a linear fit (removes slow drift / DC offset).
 * Uses least-squares linear regression.
 */
function detrend(signal: number[]): number[] {
  const n = signal.length;
  if (n < 2) return signal.slice();

  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += signal[i];
    sumXY += i * signal[i];
    sumX2 += i * i;
  }
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return signal.slice();

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  return signal.map((v, i) => v - (slope * i + intercept));
}

/**
 * Simple IIR bandpass filter (Butterworth-inspired, 2nd order).
 * Passes ~0.7–4 Hz for heart rate detection.
 * Coefficients pre-computed for 30 Hz sample rate.
 */
function bandpassFilter(signal: number[], sampleRate: number): number[] {
  const n = signal.length;
  if (n < 4) return signal.slice();

  // Normalised cutoff frequencies
  const lowCut = 0.7 / (sampleRate / 2);
  const highCut = 4.0 / (sampleRate / 2);

  // Approximate 2nd-order Butterworth bandpass via two cascaded first-order filters
  // High-pass stage (removes below lowCut)
  const alphaHP = 1 / (1 + 2 * Math.PI * lowCut);
  const hp: number[] = new Array(n).fill(0);
  hp[0] = signal[0];
  for (let i = 1; i < n; i++) {
    hp[i] = alphaHP * (hp[i - 1] + signal[i] - signal[i - 1]);
  }

  // Low-pass stage (removes above highCut)
  const alphaLP = 2 * Math.PI * highCut / (2 * Math.PI * highCut + 1);
  const lp: number[] = new Array(n).fill(0);
  lp[0] = hp[0];
  for (let i = 1; i < n; i++) {
    lp[i] = lp[i - 1] + alphaLP * (hp[i] - lp[i - 1]);
  }

  return lp;
}

/**
 * Z-score normalisation: zero mean, unit variance.
 */
function normalise(signal: number[]): number[] {
  const n = signal.length;
  if (n === 0) return [];
  const mean = signal.reduce((a, b) => a + b, 0) / n;
  const variance = signal.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
  const std = Math.sqrt(variance) || 1;
  return signal.map((v) => (v - mean) / std);
}

/**
 * Robust peak detection using adaptive threshold.
 * Enforces minimum refractory period to reject noise peaks.
 * Returns indices of detected peaks.
 */
function detectPeaks(signal: number[], sampleRate: number, minBPM: number, maxBPM: number): number[] {
  const n = signal.length;
  const minPeakDistance = Math.floor(sampleRate * 60 / maxBPM); // min samples between peaks
  const maxPeakDistance = Math.floor(sampleRate * 60 / minBPM); // max samples between peaks

  // Adaptive threshold: 60% of rolling max
  const windowSize = Math.min(n, Math.floor(sampleRate * 2));
  const peaks: number[] = [];
  let lastPeakIdx = -minPeakDistance;

  for (let i = 1; i < n - 1; i++) {
    // Local maximum check
    if (signal[i] <= signal[i - 1] || signal[i] <= signal[i + 1]) continue;

    // Refractory period
    if (i - lastPeakIdx < minPeakDistance) continue;

    // Adaptive threshold: peak must exceed 40% of local window max
    const windowStart = Math.max(0, i - windowSize);
    let localMax = -Infinity;
    for (let j = windowStart; j <= i; j++) {
      if (signal[j] > localMax) localMax = signal[j];
    }
    if (signal[i] < localMax * 0.4) continue;

    // Reject if too far from last peak (motion artefact)
    if (peaks.length > 0 && i - lastPeakIdx > maxPeakDistance) {
      // Allow but flag — handled by quality scoring
    }

    peaks.push(i);
    lastPeakIdx = i;
  }

  return peaks;
}

/**
 * Compute inter-beat intervals (IBI) in milliseconds from peak indices.
 */
function computeIBI(peakIndices: number[], sampleRate: number): number[] {
  if (peakIndices.length < 2) return [];
  const ibi: number[] = [];
  for (let i = 1; i < peakIndices.length; i++) {
    const intervalSamples = peakIndices[i] - peakIndices[i - 1];
    ibi.push((intervalSamples / sampleRate) * 1000); // ms
  }
  return ibi;
}

/**
 * Compute RMSSD (Root Mean Square of Successive Differences) from IBI array.
 * Primary HRV metric for parasympathetic activity.
 */
function computeRMSSD(ibi: number[]): number {
  if (ibi.length < 2) return 0;
  let sumSqDiff = 0;
  for (let i = 1; i < ibi.length; i++) {
    const diff = ibi[i] - ibi[i - 1];
    sumSqDiff += diff * diff;
  }
  return Math.sqrt(sumSqDiff / (ibi.length - 1));
}

/**
 * Signal quality scoring (0–1).
 * Penalises: overexposure, underexposure, low variance, irregular IBI.
 */
function computeQuality(
  rawSignal: number[],
  ibi: number[],
  redMean: number
): number {
  if (rawSignal.length < 10) return 0;

  let score = 1.0;

  // Overexposure: red channel saturated (>240)
  if (redMean > 240) score -= 0.5;

  // Underexposure: red channel too dark (<30) — finger not placed
  if (redMean < 30) score -= 0.8;

  // Low variance: signal too flat (no pulsation)
  const mean = rawSignal.reduce((a, b) => a + b, 0) / rawSignal.length;
  const variance = rawSignal.reduce((a, b) => a + (b - mean) ** 2, 0) / rawSignal.length;
  if (variance < 0.5) score -= 0.4;

  // IBI regularity: coefficient of variation should be < 30%
  if (ibi.length >= 3) {
    const ibiMean = ibi.reduce((a, b) => a + b, 0) / ibi.length;
    const ibiStd = Math.sqrt(ibi.reduce((a, b) => a + (b - ibiMean) ** 2, 0) / ibi.length);
    const cv = ibiStd / (ibiMean || 1);
    if (cv > 0.3) score -= cv * 0.5;
  } else {
    score -= 0.2; // Not enough peaks yet
  }

  return Math.max(0, Math.min(1, score));
}

// ─── Signal Processor Class ───────────────────────────────────────────────────

export class SignalProcessor {
  private config: ProcessorConfig;
  private rawBuffer: CircularBuffer;
  private lastRedMean = 128;

  constructor(config: ProcessorConfig) {
    this.config = config;
    const bufferSize = Math.ceil(config.sampleRate * config.windowSeconds);
    this.rawBuffer = new CircularBuffer(bufferSize);
  }

  /**
   * Push a new red channel sample and return updated metrics.
   * Returns null if insufficient data for reliable computation.
   */
  process(redMean: number): PPGMetrics | null {
    this.lastRedMean = redMean;
    this.rawBuffer.push(redMean);

    // Need at least 3 seconds of data
    const minSamples = this.config.sampleRate * 3;
    if (this.rawBuffer.size < minSamples) return null;

    const raw = this.rawBuffer.toArray();

    // ── Processing pipeline ──────────────────────────────────────────────────
    // 1. Detrend: remove slow drift
    const detrended = detrend(raw);

    // 2. Bandpass filter: isolate cardiac frequency band (0.7–4 Hz)
    const filtered = bandpassFilter(detrended, this.config.sampleRate);

    // 3. Normalise: zero mean, unit variance
    const normalised = normalise(filtered);

    // 4. Peak detection
    const peaks = detectPeaks(normalised, this.config.sampleRate, this.config.minBPM, this.config.maxBPM);

    // 5. IBI computation
    const ibi = computeIBI(peaks, this.config.sampleRate);

    // 6. Heart rate from median IBI (robust to outliers)
    let heartRate = 0;
    if (ibi.length >= 2) {
      const sorted = [...ibi].sort((a, b) => a - b);
      const medianIBI = sorted[Math.floor(sorted.length / 2)];
      heartRate = Math.round(60000 / medianIBI);
      // Clamp to physiological range
      heartRate = Math.max(this.config.minBPM, Math.min(this.config.maxBPM, heartRate));
    }

    // 7. RMSSD
    const rmssd = computeRMSSD(ibi);

    // 8. Signal quality
    const signalQuality = computeQuality(raw, ibi, this.lastRedMean);

    return {
      heartRate,
      ibiArray: ibi.slice(-20), // Keep last 20 IBIs
      rmssd: Math.round(rmssd * 10) / 10,
      signalQuality: Math.round(signalQuality * 100) / 100,
    };
  }

  /**
   * Get the current processed waveform for display (last N samples, normalised).
   */
  getWaveform(displaySamples = 150): number[] {
    const raw = this.rawBuffer.toArray();
    if (raw.length < 4) return [];
    const detrended = detrend(raw);
    const filtered = bandpassFilter(detrended, this.config.sampleRate);
    const normalised = normalise(filtered);
    return normalised.slice(-displaySamples);
  }

  reset(): void {
    const bufferSize = Math.ceil(this.config.sampleRate * this.config.windowSeconds);
    this.rawBuffer = new CircularBuffer(bufferSize);
  }
}
